import type { LoaderFunctionArgs } from 'react-router';
import { useLoaderData } from 'react-router';
import { GenericError } from '@auth0/auth0-spa-js';
import { postAccountIntegrateExecuteWithToken } from '../../api/apiClient';
import { auth0Client } from '../../utils/auth/auth0Client';
import { Unauthorized } from '../../components/Error/Unauthorized';
import { IntegrationComplete } from '../../features/integration/components/IntegrationComplete';
import { IntegrationGuarantorEmailConflict } from '../../features/integration/components/IntegrationGuarantorEmailConflict';

export async function integrationVerifyLoader({
  request,
}: LoaderFunctionArgs): Promise<{ status: number; returnUrl?: string; redirectingToLogin?: boolean }> {
  const url = new URL(request.url);
  try {
    const token = url.searchParams.get('token');
    if (!token) {
      return { status: 401 };
    }

    const { data, response } = await postAccountIntegrateExecuteWithToken({ token });

    if (!response.ok) {
      return { status: response.status };
    }
    // 完了画面を必ず挟み、ログイン元アプリへ戻るかどうかはユーザーの操作に委ねる。
    // returnUrl はログイン元アプリを特定できない場合 (best-effort) に省略され、その場合は戻り先をマイページ内とする
    return { status: response.status, returnUrl: data?.returnUrl };
  } catch (e) {
    // セッションの無いブラウザでメールリンクを開いた場合は、ログイン完了後に token 付きの本画面へ戻して統合を再開する
    if (e instanceof GenericError && e.error === 'login_required') {
      await auth0Client.loginWithRedirect({
        appState: { returnTo: `${url.pathname}${url.search}` },
        authorizationParams: {
          redirect_uri: `${window.location.origin}/home`,
        },
      });
      return { status: 401, redirectingToLogin: true };
    }
    // 実行中のエラーは認証エラーとして処理する
    return { status: 401 };
  } finally {
    // search param を削除
    history.replaceState(null, '', location.pathname);
  }
}

export function IntegrationVerifyPage() {
  const { status, returnUrl, redirectingToLogin } = useLoaderData<typeof integrationVerifyLoader>();
  // loginWithRedirect によるページ遷移が完了するまで何も表示しない
  if (redirectingToLogin) {
    return null;
  }
  switch (status) {
    case 201: {
      return <IntegrationComplete returnUrl={returnUrl} />;
    }
    // 保護者メール衝突の場合のみ明示的にユーザに知らせる
    case 409: {
      return <IntegrationGuarantorEmailConflict />;
    }
    default: {
      return <Unauthorized />;
    }
  }
}
